<template>
  <div class="position-relative" ref="selector">
    <!-- Input Trigger -->
    <div class="input-group" @click="toggleDropdown">
      <span class="input-group-text bg-white border-end-0">
        <i :class="modelValue || 'fas fa-icons'" class="text-dark"></i>
      </span>
      <input
        type="text"
        class="form-control ps-2 cursor-pointer bg-white"
        :value="modelValue"
        readonly
        placeholder="Select Icon..."
        style="border-left: none"
      />
      <button
        v-if="modelValue"
        class="btn btn-outline-secondary border-start-0"
        type="button"
        @click.stop="clearSelection"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Dropdown Panel -->
    <div
      v-if="isOpen"
      class="card position-absolute w-100 shadow-lg z-index-3 mt-1"
      style="z-index: 1000; max-height: 300px"
    >
      <div class="p-2 border-bottom sticky-top bg-white">
        <input
          ref="searchInput"
          v-model="searchQuery"
          class="form-control form-control-sm"
          placeholder="Search icons (e.g. star, user)..."
          @click.stop
        />
      </div>
      <div class="card-body p-2 overflow-auto" style="max-height: 250px">
        <div class="d-flex flex-wrap gap-2 justify-content-center">
          <div
            v-for="icon in filteredIcons"
            :key="icon"
            class="icon-item d-flex align-items-center justify-content-center border rounded cursor-pointer transition-all"
            :class="{
              'bg-dark text-white': modelValue === icon,
              'bg-light text-dark': modelValue !== icon,
            }"
            style="width: 40px; height: 40px"
            @click="selectIcon(icon)"
            :title="icon"
          >
            <i :class="icon"></i>
          </div>
          <div
            v-if="filteredIcons.length === 0"
            class="text-muted text-xs w-100 text-center py-2"
          >
            No icons found.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "IconSelector",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      // Curated list of FontAwesome 5/6 Free icons common in e-commerce
      icons: [
        // Basics
        "fas fa-star",
        "far fa-star",
        "fas fa-check",
        "fas fa-check-circle",
        "fas fa-times",
        "fas fa-info-circle",
        "fas fa-exclamation-circle",
        "fas fa-question-circle",

        // E-commerce & Shopping
        "fas fa-shopping-cart",
        "fas fa-shopping-bag",
        "fas fa-store",
        "fas fa-store-alt",
        "fas fa-tag",
        "fas fa-tags",
        "fas fa-receipt",
        "fas fa-credit-card",
        "fas fa-wallet",
        "fas fa-truck",
        "fas fa-shipping-fast",
        "fas fa-box",
        "fas fa-box-open",
        "fas fa-gift",
        "fas fa-percent",
        "fas fa-money-bill-wave",

        // User & Social
        "fas fa-user",
        "fas fa-users",
        "fas fa-user-circle",
        "fas fa-heart",
        "far fa-heart",
        "fas fa-thumbs-up",
        "fas fa-thumbs-down",
        "fas fa-comment",
        "fas fa-comments",
        "fas fa-share-alt",
        "fas fa-envelope",
        "fas fa-phone",

        // UI & Navigation
        "fas fa-home",
        "fas fa-search",
        "fas fa-filter",
        "fas fa-sort",
        "fas fa-bars",
        "fas fa-arrow-right",
        "fas fa-arrow-left",
        "fas fa-chevron-right",
        "fas fa-chevron-down",
        "fas fa-edit",
        "fas fa-trash",
        "fas fa-plus",
        "fas fa-minus",
        "fas fa-cog",

        // Features & Characteristics
        "fas fa-leaf",
        "fas fa-fire",
        "fas fa-water",
        "fas fa-bolt",
        "fas fa-wind",
        "fas fa-snowflake",
        "fas fa-sun",
        "fas fa-moon",
        "fas fa-cloud",
        "fas fa-shield-alt",
        "fas fa-lock",
        "fas fa-unlock",
        "fas fa-key",
        "fas fa-medal",
        "fas fa-trophy",
        "fas fa-award",
        "fas fa-certificate",
        "fas fa-crown",
        "fas fa-gem",
        "fas fa-magic",
        "fas fa-rocket",
        "fas fa-plane",
        "fas fa-globe",
        "fas fa-map-marker-alt",
        "fas fa-clock",
        "fas fa-calendar-alt",
        "fas fa-stopwatch",

        // Clothes & specifics (if available in free set)
        "fas fa-tshirt",
        "fas fa-socks",
        "fas fa-hat-cowboy",
        "fas fa-graduation-cap",
        "fas fa-ruler",
        "fas fa-cut",
        "fas fa-palette",
        "fas fa-image",
        "fas fa-camera",
        "fas fa-video",
      ],
    };
  },
  computed: {
    filteredIcons() {
      if (!this.searchQuery) return this.icons;
      const q = this.searchQuery.toLowerCase();
      // Simple search: match icon class string
      return this.icons.filter((icon) => icon.includes(q));
    },
  },
  mounted() {
    document.addEventListener("click", this.closeDropdownOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdownOutside);
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },
    closeDropdownOutside(e) {
      if (this.$refs.selector && !this.$refs.selector.contains(e.target)) {
        this.isOpen = false;
      }
    },
    selectIcon(icon) {
      this.$emit("update:modelValue", icon);
      this.isOpen = false;
    },
    clearSelection() {
      this.$emit("update:modelValue", "");
    },
  },
};
</script>

<style scoped>
.icon-item:hover {
  background-color: #f8f9fa; /* Light gray on hover if not selected */
  transform: scale(1.1);
  border-color: #343a40 !important;
}
.z-index-3 {
  z-index: 3 !important;
}
</style>
